import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { paymentProvidersApi } from "../lib/api";
import {
  Sliders, Loader2, Save, Percent, CreditCard, HandCoins, ShieldCheck,
  Zap, Wallet, Mail, Info, Check,
} from "lucide-react";

/** Behaviour toggles: deposits, partial payments, tips, CVV, capture, save
 *  cards, email receipts + accepted methods (filtered against the tenant's
 *  currently-connected processors). Companies without any online processor
 *  can still enable offline methods (cash/check/etc.). */
export default function PaymentBehaviorCard() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const d = await paymentProvidersApi.getBehavior();
      setState(d);
      setDirty(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load payment behaviour");
    }
  };
  useEffect(() => { load(); }, []);

  const patch = (partial) => {
    setState((s) => ({ ...s, settings: { ...s.settings, ...partial } }));
    setDirty(true);
  };
  const toggleMethod = (id) => {
    const has = state.settings.acceptedMethods.includes(id);
    patch({
      acceptedMethods: has
        ? state.settings.acceptedMethods.filter((m) => m !== id)
        : [...state.settings.acceptedMethods, id],
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const d = await paymentProvidersApi.saveBehavior(state.settings);
      setState(d);
      setDirty(false);
      toast.success("Payment behaviour saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const online = useMemo(
    () => (state?.methods || []).filter((m) => !m.offline),
    [state]
  );
  const offline = useMemo(
    () => (state?.methods || []).filter((m) => m.offline),
    [state]
  );
  const anyOnlineAvailable = online.some((m) => m.available);

  if (!state) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 mt-5" data-testid="payment-behavior-card">
        <div className="text-center py-6 text-slate-400">
          <Loader2 className="h-4 w-4 inline animate-spin" />
        </div>
      </div>
    );
  }

  const s = state.settings;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mt-5" data-testid="payment-behavior-card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sliders className="h-4 w-4 text-indigo-600" />
          <h2 className="text-[14px] font-semibold text-slate-900">Payment behaviour</h2>
        </div>
        <Button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="pb-save"
          className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-[12.5px] font-semibold px-3 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
          Save
        </Button>
      </div>
      <p className="text-[12px] text-slate-500 mb-4">
        Rules that apply company-wide to every invoice, estimate deposit, and Pay Now link — regardless
        of which processor is charged.
      </p>

      {/* Row: Deposits */}
      <ToggleRow
        testid="pb-allow-deposits"
        icon={HandCoins}
        title="Allow deposits on estimates"
        subtitle="Ask customers for a partial payment before scheduling the work."
        checked={s.allowDeposits}
        onChange={(v) => patch({ allowDeposits: v })}
      >
        {s.allowDeposits && (
          <PctField
            label="Default deposit"
            testid="pb-deposit-pct"
            value={s.defaultDepositPct}
            onChange={(v) => patch({ defaultDepositPct: v })}
          />
        )}
      </ToggleRow>

      {/* Row: Partial payments */}
      <ToggleRow
        testid="pb-allow-partial"
        icon={Wallet}
        title="Allow partial invoice payments"
        subtitle="Customers can pay any amount toward the balance until it's zero."
        checked={s.allowPartialPayments}
        onChange={(v) => patch({ allowPartialPayments: v })}
      >
        {s.allowPartialPayments && (
          <PctField
            label="Minimum"
            testid="pb-partial-min"
            value={s.minPartialPct}
            onChange={(v) => patch({ minPartialPct: v })}
          />
        )}
      </ToggleRow>

      {/* Row: Tips */}
      <ToggleRow
        testid="pb-allow-tips"
        icon={Zap}
        title="Allow tipping at checkout"
        subtitle="Show tip presets on the customer pay page after the total."
        checked={s.allowTips}
        onChange={(v) => patch({ allowTips: v })}
      >
        {s.allowTips && (
          <TipPresetsField
            presets={s.tipPresets}
            onChange={(v) => patch({ tipPresets: v })}
          />
        )}
      </ToggleRow>

      {/* Row: Require CVV */}
      <ToggleRow
        testid="pb-require-cvv"
        icon={ShieldCheck}
        title="Require CVV on every card charge"
        subtitle="Even for saved cards. Disable only for recurring installments."
        checked={s.requireCVV}
        onChange={(v) => patch({ requireCVV: v })}
      />

      {/* Row: Auto capture */}
      <ToggleRow
        testid="pb-auto-capture"
        icon={CreditCard}
        title="Auto-capture card payments"
        subtitle={
          s.autoCapture
            ? "Funds settle immediately when the customer pays."
            : "Authorize only — office must manually capture within 7 days."
        }
        checked={s.autoCapture}
        onChange={(v) => patch({ autoCapture: v })}
      />

      {/* Row: Save cards */}
      <ToggleRow
        testid="pb-save-cards"
        icon={Wallet}
        title="Save cards for repeat customers"
        subtitle="Tokenize cards on file so members can pay with one click next time."
        checked={s.saveCards}
        onChange={(v) => patch({ saveCards: v })}
      />

      {/* Row: Email receipts */}
      <ToggleRow
        testid="pb-email-receipts"
        icon={Mail}
        title="Email receipt on successful payment"
        subtitle="Sent from your configured email — includes invoice PDF."
        checked={s.emailReceipts}
        onChange={(v) => patch({ emailReceipts: v })}
        last
      />

      {/* Accepted methods */}
      <div className="mt-5">
        <div className="flex items-center gap-2 mb-1">
          <CreditCard className="h-3.5 w-3.5 text-slate-500" />
          <div className="text-[12.5px] font-semibold text-slate-900">Accepted payment methods</div>
        </div>
        <p className="text-[11.5px] text-slate-500 mb-2.5">
          Filtered by what your connected processors support.
          {!anyOnlineAvailable && " Connect a processor above to unlock online methods."}
        </p>

        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Online</div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {online.map((m) => (
            <MethodChip
              key={m.id}
              method={m}
              checked={s.acceptedMethods.includes(m.id)}
              onToggle={() => toggleMethod(m.id)}
            />
          ))}
        </div>

        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Offline</div>
        <div className="flex flex-wrap gap-1.5">
          {offline.map((m) => (
            <MethodChip
              key={m.id}
              method={m}
              checked={s.acceptedMethods.includes(m.id)}
              onToggle={() => toggleMethod(m.id)}
            />
          ))}
        </div>
      </div>

      {dirty && (
        <div className="mt-4 flex items-center gap-2 text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="pb-dirty-banner">
          <Info className="h-3.5 w-3.5" />
          Unsaved changes — click Save to apply.
        </div>
      )}
    </div>
  );
}

function ToggleRow({ icon: Icon, title, subtitle, checked, onChange, children, testid, last }) {
  return (
    <div className={`flex items-start gap-3 py-2.5 ${last ? "" : "border-b border-slate-100"}`}>
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-slate-900">{title}</div>
        <div className="text-[11.5px] text-slate-500 mt-0.5">{subtitle}</div>
        {children && <div className="mt-2">{children}</div>}
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        data-testid={testid}
        className="data-[state=checked]:bg-indigo-600 mt-0.5"
      />
    </div>
  );
}

function PctField({ label, value, onChange, testid }) {
  return (
    <div className="inline-flex items-center gap-2">
      <span className="text-[11.5px] text-slate-500">{label}</span>
      <div className="relative">
        <input
          type="number"
          min="0"
          max="100"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
          data-testid={testid}
          className="w-16 h-8 pl-2 pr-6 rounded-md border border-slate-200 bg-white text-[12.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
        />
        <Percent className="h-3 w-3 text-slate-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
    </div>
  );
}

function TipPresetsField({ presets, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const n = parseInt(input, 10);
    if (!Number.isFinite(n) || n < 1 || n > 100) return;
    if (presets.includes(n)) { setInput(""); return; }
    onChange([...presets, n].sort((a, b) => a - b).slice(0, 6));
    setInput("");
  };
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[11.5px] text-slate-500">Presets:</span>
      {presets.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(presets.filter((x) => x !== p))}
          data-testid={`pb-tip-preset-${p}`}
          className="h-7 px-2 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 text-[11.5px] font-semibold hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700 transition-colors"
          title="Click to remove"
        >
          {p}%
        </button>
      ))}
      {presets.length < 6 && (
        <div className="relative">
          <input
            type="number"
            min="1"
            max="100"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            onBlur={add}
            placeholder="Add %"
            data-testid="pb-tip-preset-input"
            className="w-16 h-7 pl-2 pr-5 rounded-md border border-dashed border-slate-300 bg-white text-[11.5px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
          />
          <Percent className="h-3 w-3 text-slate-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      )}
    </div>
  );
}

function MethodChip({ method, checked, onToggle }) {
  const { id, label, available } = method;
  const disabled = !available;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onToggle}
      data-testid={`pb-method-${id}`}
      data-available={available}
      disabled={disabled}
      title={disabled ? "Not supported by any connected processor" : undefined}
      className={[
        "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border text-[12px] font-medium transition-colors",
        disabled
          ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed line-through"
          : checked
            ? "border-indigo-600 bg-indigo-600 text-white shadow-sm"
            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
      ].join(" ")}
    >
      {checked && !disabled && <Check className="h-3 w-3" />}
      {label}
    </button>
  );
}
